import { useSelector } from 'react-redux';
import { ORDER_METHODS } from '../../services/exports/Constants';
import HelperMethods from '../../services/exports/HelperMethods';
import { StoreInterface } from '../../store/types';
import useCompanyHours from '../availability/useCompanyHours';
import useOpeningStatus from '../availability/useOpeningStatus';
import useOrderMethodsStatus from '../availability/useOrderMethodsStatus';
import useOrderPreparationTime from './useOrderPreparationTime';
import useAppMethods from '../utility/useAppMethods';

export interface TableOrderStatusInterface {
  state: 'open' | 'closed' | 'paused';
  /** "Opens today at 13:00", for the closed state. */
  openingText: string;
  /**
   * When table orders open again: "13:00" later today, "Friday 13:00" on another day. Null while open
   * or paused, and while closed without a next opening to name (a holiday, outside ordering times).
   */
  opensAt: string | null;
  /** "10-20": pickup's preparation time, the restaurant's longer one while it is busy. */
  servedIn: string;
}

/** Whether a table order can be placed now. Table ordering follows pickup's hours and pauses. */
export default function useTableOrderStatus(): TableOrderStatusInterface {
  const company = useSelector((state: StoreInterface) => state.initialData.company);
  const companyHours = useCompanyHours({ data: company, orderMethod: ORDER_METHODS.PICKUP });
  const { isOrderMethodCurrentlyOpen } = useOrderMethodsStatus();
  const { details_text: openingText } = useOpeningStatus({
    orderMethod: ORDER_METHODS.PICKUP,
    closedPrefixType: 'closed',
  });
  const getPreparationTime = useOrderPreparationTime(company);
  const { formatScheduleUnit } = useAppMethods();

  const state = companyHours?.ordersArePaused
    ? 'paused'
    : isOrderMethodCurrentlyOpen(ORDER_METHODS.ON_SITE)
      ? 'open'
      : 'closed';

  return {
    state,
    openingText,
    opensAt: state === 'closed' ? nextOpening() : null,
    servedIn: HelperMethods.formatTimeInterval(getPreparationTime(ORDER_METHODS.PICKUP)),
  };

  function nextOpening(): string | null {
    if (!companyHours || companyHours.closedUntil || companyHours.open) {
      return null;
    }

    if (companyHours.opensToday?.status) {
      return formatScheduleUnit(companyHours.opensToday.time);
    }

    return companyHours.opensStartingTomorrow?.time
      ? formatScheduleUnit(companyHours.opensStartingTomorrow.time, { day: true, time: true })
      : null;
  }
}
